import type { BlogPost } from "./BlogPostPage";
import { doorwayBlogAuthor } from "./DoorwayTeamsPostData";

export const hostWithoutGitPath = "/blog/host-web-app-without-git";
export const whereToHostVibeCodedAppPath = "/blog/where-to-host-vibe-coded-app";
export const internalToolHostingPath = "/blog/internal-tool-hosting-marketing-teams";
export const shareClaudeSkillsPath = "/blog/share-claude-code-skills-team";
export const shareVibeCodedAppPath = "/blog/share-vibe-coded-app";

export const hostWithoutGitPost: BlogPost = {
  path: hostWithoutGitPath,
  title: "9 Best Ways to Host a Web App Without Git in 2026",
  description: "Compare nine ways to host an AI-built web app without Git, including browser uploads, agent publishing, pricing, access, and honest tradeoffs.",
  publishedAt: "2026-07-25",
  author: doorwayBlogAuthor,
  image: "/header-images/host-web-app-without-git.png",
  imageAlt: "A local project folder passing through an open doorway and becoming a live browser app.",
  imageWidth: 1672,
  imageHeight: 941,
  readingTime: "10 min read",
  tags: ["hosting without Git", "AI-built apps", "Claude Code", "Codex", "marketing engineering"],
  faqs: [
    {
      question: "What does hosting without Git mean?",
      answer: "It means publishing files from your computer without first storing them in a GitHub or GitLab repository. The host accepts a folder, ZIP, local directory, or agent-prepared upload directly.",
    },
    {
      question: "Can I host a React or Next.js app without Git?",
      answer: "Yes. React projects can be published from finished build output. Next.js can use static export when it does not need server features, while Vercel CLI can deploy a local Next.js project directly.",
    },
    {
      question: "How can I get a password-protected web app?",
      answer: "Doorway Solo includes password protection for $4.99 per month. Tiiny Host Solo and Replit also offer password-protected publishing paths at their current plan prices.",
    },
    {
      question: "Can Claude Code or Codex publish my app?",
      answer: "Yes. Doorway provides a publishing skill for Claude Code and Codex. The agent prepares and uploads the project while you approve the destination and access settings in your browser.",
    },
    {
      question: "Do Netlify Drop links expire?",
      answer: "Netlify does not describe an inherent expiration for claimed Drop projects. You can return to the project dashboard and upload updated versions.",
    },
  ],
};

export const whereToHostVibeCodedAppPost: BlogPost = {
  path: whereToHostVibeCodedAppPath,
  title: "Where Should You Host a Vibe-Coded App? 7 Options for Marketers",
  description: "A plain-language comparison of seven places to host a vibe-coded app, ranked for marketers by setup, price, access, updates, and runtime support.",
  publishedAt: "2026-07-25",
  author: doorwayBlogAuthor,
  image: "/header-images/where-to-host-vibe-coded-app.png",
  imageAlt: "A local app folder choosing among several hosting paths before reaching a green open doorway.",
  imageWidth: 1672,
  imageHeight: 941,
  readingTime: "9 min read",
  featured: true,
  tags: ["vibe-coded app hosting", "AI app hosting", "marketers", "Claude Code", "Doorway"],
  faqs: [
    {
      question: "Where should a non-developer host a vibe-coded app?",
      answer: "Doorway is the strongest fit for a finished local project because supported publishing does not require Git, a CLI, or deployment configuration. Netlify Drop is a strong free alternative.",
    },
    {
      question: "Can I host an AI-built app without Git?",
      answer: "Yes. Doorway, Netlify Drop, Cloudflare Pages Direct Upload, Tiiny Host, Replit, Vercel CLI, and Railway CLI all provide no-Git paths.",
    },
    {
      question: "Which host is best for a Next.js app?",
      answer: "Vercel remains a natural choice when the project depends on Next.js server features. A statically exported Next.js project can also use Doorway, Netlify, or Cloudflare Pages.",
    },
    {
      question: "Which host is best for a backend and database?",
      answer: "Railway is a friendly starting point for a backend and database. Render suits multi-service applications, while Replit is convenient when building and hosting happen in the same product.",
    },
    {
      question: "Can Claude Code or Codex deploy for me?",
      answer: "Yes. Doorway's agent publishing skill handles preparation and upload. You approve the exact project and access settings in your browser.",
    },
  ],
};

export const internalToolHostingPost: BlogPost = {
  path: internalToolHostingPath,
  title: "7 Best Internal Tool Hosting Platforms for Marketing Teams in 2026",
  description: "Compare internal tool hosts for marketing teams by private access, ownership, roles, offboarding, pricing, publishing, and runtime support.",
  publishedAt: "2026-07-25",
  author: doorwayBlogAuthor,
  image: "/header-images/internal-tool-hosting-marketing-teams.png",
  imageAlt: "A private library of internal app cards behind a green team access doorway.",
  imageWidth: 1672,
  imageHeight: 941,
  readingTime: "10 min read",
  tags: ["internal tool hosting", "marketing teams", "private apps", "team access", "Doorway Teams"],
  faqs: [
    {
      question: "Can I host an AI-built internal tool without a developer?",
      answer: "Yes, when the tool produces finished static output or a supported source project. Claude Code and Codex can publish through Doorway while a creator approves the destination and access settings in the browser.",
    },
    {
      question: "What is the cheapest way to host a private internal tool?",
      answer: "For a shared-password workflow, Doorway Solo starts at $4.99 per month. Replit also offers private and password-protected publishing. Compare usage and runtime needs before choosing.",
    },
    {
      question: "Do I need to pay for every internal viewer?",
      answer: "That depends on the platform. Doorway Team costs $99 per workspace and includes unlimited approved viewers. Application access and platform collaboration seats are separate concepts on many developer hosts.",
    },
    {
      question: "What happens when somebody leaves the company?",
      answer: "Doorway Team lets an Owner remove the member and revoke active sessions. Shared workspace ownership keeps apps with the company.",
    },
    {
      question: "Is password protection enough for an internal tool?",
      answer: "It can work for a short review or small trusted group. Approved membership gives stronger ongoing control because access and revocation are individual.",
    },
  ],
};

export const shareClaudeSkillsPost: BlogPost = {
  path: shareClaudeSkillsPath,
  title: "How to Share Claude Code Skills With Your Team: 5 Methods",
  description: "Compare five ways to share Claude Code skills with a team, from project folders and plugin marketplaces to managed organization-wide updates.",
  publishedAt: "2026-07-25",
  author: doorwayBlogAuthor,
  image: "/header-images/share-claude-code-skills-team.png",
  imageAlt: "One approved AI skill flowing through a protected hub to several team laptops.",
  imageWidth: 1672,
  imageHeight: 941,
  readingTime: "9 min read",
  tags: ["share Claude skills", "Claude Code skills", "Agent Skills", "marketing teams", "Doorway Team Skills"],
  faqs: [
    {
      question: "What is a Claude Code skill?",
      answer: "A Claude Code skill is a folder with a SKILL.md entrypoint and optional supporting files. Claude loads it when relevant or when a user invokes it directly.",
    },
    {
      question: "Where do Claude Code skills live?",
      answer: "Personal skills live under ~/.claude/skills. Project skills live under .claude/skills in a project. Plugins and managed organization settings provide additional distribution scopes.",
    },
    {
      question: "Can I share a Claude skill without Git?",
      answer: "A folder can be copied manually without Git. Doorway's Team Skills direction adds a managed no-Git path with version history, review, signed updates, and local-edit protection, but broad availability remains gated by its release checks.",
    },
    {
      question: "Are Claude Code skills the same as MCP servers?",
      answer: "No. Skills contain instructions and supporting resources that guide Claude. MCP servers connect Claude to external tools and data. A skill can depend on an MCP server.",
    },
    {
      question: "Can Doorway share one skill across Claude, Codex, and Gemini?",
      answer: "Doorway's current Team Skills boundary begins with standard Agent Skills for local Claude Code on macOS. Codex, Gemini, and other adapters remain planned until their compatibility paths are implemented and verified.",
    },
  ],
};

export const shareVibeCodedAppPost: BlogPost = {
  path: shareVibeCodedAppPath,
  title: "5 Ways to Share a Vibe-Coded App With Clients or Coworkers",
  description: "Choose the right way to share a vibe-coded app, from a public link or client password to approved team access and safe revision updates.",
  publishedAt: "2026-07-25",
  author: doorwayBlogAuthor,
  image: "/header-images/share-vibe-coded-app.png",
  imageAlt: "A vibe-coded app branching into public, password-protected, and approved-team sharing paths.",
  imageWidth: 1672,
  imageHeight: 941,
  readingTime: "8 min read",
  tags: ["share vibe-coded app", "client preview", "password-protected app", "team app", "Doorway"],
  faqs: [
    {
      question: "What is the easiest way to share a vibe-coded app?",
      answer: "Publish a stable browser link from the finished local project. Doorway is designed for that handoff and can publish supported projects without Git.",
    },
    {
      question: "Should I send a ZIP file to a client?",
      answer: "A ZIP is useful for transferring source files, but it is a poor review experience. A hosted link opens on any device and keeps setup out of the client's hands.",
    },
    {
      question: "When should I use a password-protected link?",
      answer: "Use a password for a client preview, proof of concept, or small trusted group that does not need individual accounts.",
    },
    {
      question: "When should I use approved team access?",
      answer: "Use approved membership for ongoing company tools where access must follow individual identities, roles, and offboarding.",
    },
    {
      question: "Will updating the app break the shared link?",
      answer: "A stable host should let you publish a new revision without changing the URL. Doorway keeps immutable revisions and does not replace the live app when a candidate update fails.",
    },
  ],
};

export const seoAeoBlogPosts = [
  hostWithoutGitPost,
  whereToHostVibeCodedAppPost,
  internalToolHostingPost,
  shareClaudeSkillsPost,
  shareVibeCodedAppPost,
];
